import React, { useState } from 'react';
import { userAPI } from '../services/api';

interface CheckoutCartProps {
  cart: any[];
  onClose: () => void;
  onOrderPlaced: () => void;
  selectedLocation: any;
  userAddress: string;
  onRemoveItem?: (itemId: string, variantIndex?: number) => void;
  onUpdateQuantity?: (itemId: string, newQuantity: number, variantIndex?: number) => void;
}

const CheckoutCart: React.FC<CheckoutCartProps> = ({ 
  cart, 
  onClose, 
  onOrderPlaced,
  selectedLocation,
  userAddress,
  onRemoveItem,
  onUpdateQuantity
}) => {
  const [paymentMethod, setPaymentMethod] = useState('UPI');
  const [loading, setLoading] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any>(null);

  // Group cart items by store
  const groupedByStore = cart.reduce((acc: any, item) => {
    if (!acc[item.storeId]) {
      acc[item.storeId] = {
        storeId: item.storeId,
        storeName: item.storeName,
        items: []
      };
    }
    acc[item.storeId].items.push(item);
    return acc;
  }, {});

  const stores = Object.values(groupedByStore);

  // Calculate delivery charges based on categories
  const getDeliveryCharges = () => {
    // Get unique categories from cart
    const categories = Array.from(new Set(cart.map((item: any) => item.storeCategory).filter(Boolean)));
    
    if (categories.length === 0) {
      return 50; // Default delivery charge
    } else if (categories.length === 1) {
      return 50; // Single category - base delivery
    } else {
      // Multiple categories: ₹50 base + ₹20 per extra category
      return 50 + ((categories.length - 1) * 20);
    }
  };

  // Calculate totals
  const itemsTotal = cart.reduce((sum, item) => {
    const price = item.selectedVariant ? item.selectedVariant.price : item.price;
    return sum + (price * item.quantity);
  }, 0);
  const deliveryCharges = getDeliveryCharges();
  const platformFee = 5;
  const gst = (itemsTotal * 0.05); // 5% GST
  const totalAmount = itemsTotal + deliveryCharges + platformFee + gst;

  // Get categories for display
  const uniqueCategories = Array.from(new Set(cart.map((item: any) => item.storeCategory).filter(Boolean)));

  const handlePlaceOrder = async () => {
    setLoading(true);
    try {
      // Create order for each store
      const orderPromises = stores.map(async (store: any) => {
        const storeItems = store.items.map((item: any) => ({
          itemId: item._id,
          name: item.name,
          nameInTelugu: item.nameInTelugu,
          price: item.selectedVariant ? item.selectedVariant.price : item.price,
          quantity: item.quantity,
          icon: item.icon,
          color: item.color,
          variant: item.selectedVariant ? `${item.selectedVariant.quantity} ${item.selectedVariant.unit}` : undefined,
          customQuantity: item.customQuantity
        }));

        const storeTotal = store.items.reduce((sum: number, item: any) => {
          const price = item.selectedVariant ? item.selectedVariant.price : item.price;
          return sum + (price * item.quantity);
        }, 0);

        const orderData = {
          storeId: store.storeId,
          items: storeItems,
          deliveryAddress: {
            address: userAddress,
            coordinates: selectedLocation 
              ? { latitude: selectedLocation.lat, longitude: selectedLocation.lng }
              : { latitude: 17.6688, longitude: 80.8936 }
          },
          itemsTotal: storeTotal,
          deliveryCharges: deliveryCharges / stores.length, // Split delivery charge
          platformFee: platformFee / stores.length,
          gst: (storeTotal * 0.05),
          totalAmount: storeTotal + (deliveryCharges / stores.length) + (platformFee / stores.length) + (storeTotal * 0.05),
          paymentMethod: paymentMethod,
          paymentStatus: paymentMethod === 'Cash on Delivery' ? 'Pending' : 'Paid'
        };

        return await userAPI.createOrder(orderData);
      });

      const orders = await Promise.all(orderPromises);
      
      // Check if response includes success message
      const firstOrder = orders[0];
      const successMessage = firstOrder.data?.message || firstOrder.data?.notification;
      
      if (successMessage) {
        // Show success notification
        alert('✅ ' + successMessage);
      }
      
      // Show invoice for the first order (or combined invoice)
      setOrderDetails({
        orders,
        itemsTotal,
        deliveryCharges,
        platformFee,
        gst,
        totalAmount,
        paymentMethod,
        timestamp: new Date()
      });
      setShowInvoice(true);
      
    } catch (error: any) {
      console.error('Order placement error:', error);
      alert('Failed to place order: ' + (error.response?.data?.message || 'Please try again'));
      setLoading(false);
    }
  };

  const handleCloseInvoice = () => {
    setShowInvoice(false);
    onOrderPlaced();
    onClose();
  };

  if (showInvoice && orderDetails) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '20px',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          position: 'relative'
        }}>
          {/* Close Button */}
          <button
            onClick={handleCloseInvoice}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              background: 'rgba(255,255,255,0.3)',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              cursor: 'pointer',
              fontSize: '20px',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1
            }}
          >
            ✕
          </button>
          
          {/* Invoice Header */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '25px',
            borderRadius: '20px 20px 0 0',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '50px', marginBottom: '10px' }}>📦</div>
            <h2 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>Order Placed!</h2>
            <p style={{ margin: 0, opacity: 0.9 }}>Waiting for store confirmation</p>
          </div>

          {/* Invoice Details */}
          <div style={{ padding: '25px' }}>
            <div style={{ 
              background: '#f8f9fa', 
              padding: '15px', 
              borderRadius: '12px',
              marginBottom: '20px'
            }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#666' }}>Order ID</p>
              <p style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#333' }}>
                #{orderDetails.orders[0]?._id?.slice(-8).toUpperCase()}
              </p>
              <p style={{ margin: '10px 0 0 0', fontSize: '13px', color: '#666' }}>
                {orderDetails.timestamp.toLocaleString()}
              </p>
            </div>

            <h3 style={{ fontSize: '18px', marginBottom: '15px', color: '#333' }}>Order Summary</h3>
            
            {/* Items by Store */}
            {stores.map((store: any, idx: number) => (
              <div key={idx} style={{ marginBottom: '20px' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '10px 15px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '10px'
                }}>
                  📦 {store.storeName}
                </div>
                {store.items.map((item: any, itemIdx: number) => {
                  const displayPrice = item.selectedVariant ? item.selectedVariant.price : item.price;
                  return (
                    <div key={itemIdx} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '10px 0',
                      borderBottom: itemIdx < store.items.length - 1 ? '1px solid #f0f0f0' : 'none'
                    }}>
                      <div>
                        <p style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#333' }}>
                          {item.name} 
                          {item.selectedVariant && (
                            <span style={{ 
                              marginLeft: '5px', 
                              fontSize: '13px', 
                              background: '#667eea', 
                              color: 'white', 
                              padding: '2px 8px', 
                              borderRadius: '4px' 
                            }}>
                              {item.selectedVariant.quantity} {item.selectedVariant.unit}
                            </span>
                          )}
                          {item.customQuantity && (
                            <span style={{ 
                              marginLeft: '5px', 
                              fontSize: '13px', 
                              background: '#22c55e', 
                              color: 'white', 
                              padding: '2px 8px', 
                              borderRadius: '4px' 
                            }}>
                              📝 {item.customQuantity}
                            </span>
                          )}
                          {!item.selectedVariant && !item.customQuantity && ` x ${item.quantity}`}
                        </p>
                        {item.nameInTelugu && (
                          <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#666' }}>
                            {item.nameInTelugu}
                          </p>
                        )}
                        {(item.selectedVariant || item.customQuantity) && (
                          <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#999' }}>
                            Qty: {item.quantity}
                          </p>
                        )}
                      </div>
                      <p style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#667eea' }}>
                        ₹{(displayPrice * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Bill Breakdown */}
            <div style={{
              background: '#f8f9fa',
              padding: '15px',
              borderRadius: '12px',
              marginTop: '20px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', color: '#666' }}>Items Total</span>
                <span style={{ fontSize: '14px', fontWeight: '600' }}>₹{itemsTotal.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', color: '#666' }}>Delivery Charges</span>
                <span style={{ fontSize: '14px', fontWeight: '600' }}>₹{deliveryCharges.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', color: '#666' }}>Platform Fee</span>
                <span style={{ fontSize: '14px', fontWeight: '600' }}>₹{platformFee.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontSize: '14px', color: '#666' }}>GST (5%)</span>
                <span style={{ fontSize: '14px', fontWeight: '600' }}>₹{gst.toFixed(2)}</span>
              </div>
              <div style={{
                borderTop: '2px solid #ddd',
                paddingTop: '12px',
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span style={{ fontSize: '18px', fontWeight: '700', color: '#333' }}>Total Amount</span>
                <span style={{ fontSize: '20px', fontWeight: '700', color: '#667eea' }}>
                  ₹{totalAmount.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Payment Method */}
            <div style={{ 
              marginTop: '20px',
              padding: '15px',
              background: '#e8f5e9',
              borderRadius: '12px',
              border: '2px solid #4caf50'
            }}>
              <p style={{ margin: '0 0 5px 0', fontSize: '13px', color: '#666' }}>Payment Method</p>
              <p style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#2e7d32' }}>
                💳 {paymentMethod}
              </p>
            </div>

            {/* Delivery Address */}
            <div style={{ 
              marginTop: '15px',
              padding: '15px',
              background: '#fff3e0',
              borderRadius: '12px',
              border: '2px solid #ff9800'
            }}>
              <p style={{ margin: '0 0 5px 0', fontSize: '13px', color: '#666' }}>Delivery Address</p>
              <p style={{ margin: 0, fontSize: '14px', color: '#e65100' }}>
                📍 {userAddress}
              </p>
            </div>

            {/* Order Status Message */}
            <div style={{
              marginTop: '20px',
              padding: '15px',
              background: '#fff8e1',
              borderRadius: '12px',
              textAlign: 'center',
              border: '2px solid #ffc107'
            }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#f57c00', fontWeight: '600' }}>
                ⏳ Waiting for store to accept your order
              </p>
              <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#666' }}>
                You will be notified once the store confirms
              </p>
            </div>

            <button
              onClick={handleCloseInvoice}
              style={{
                width: '100%',
                marginTop: '20px',
                padding: '15px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
              }}
            >
              Track My Order
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      zIndex: 10000,
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center'
    }} onClick={onClose}>
      <div 
        style={{
          background: 'white',
          borderRadius: '25px 25px 0 0',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '85vh',
          overflow: 'auto',
          boxShadow: '0 -5px 25px rgba(0,0,0,0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          position: 'sticky',
          top: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '20px',
          borderRadius: '25px 25px 0 0',
          zIndex: 100
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700' }}>🛒 Checkout</h2>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '35px',
                height: '35px',
                fontSize: '20px',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              ✕
            </button>
          </div>
          <p style={{ margin: '5px 0 0 0', opacity: 0.9, fontSize: '14px' }}>
            {cart.reduce((sum, item) => sum + item.quantity, 0)} items in cart
          </p>
        </div>

        <div style={{ padding: '20px' }}>
          {/* Items by Store */}
          {stores.map((store: any, idx: number) => (
            <div key={idx} style={{ marginBottom: '20px' }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '700',
                marginBottom: '12px',
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '14px'
                }}>
                  {store.storeName}
                </span>
              </h3>
              {store.items.map((item: any, itemIdx: number) => {
                const itemPrice = item.selectedVariant ? item.selectedVariant.price : item.price;
                return (
                  <div key={itemIdx} style={{
                    display: 'flex',
                    gap: '12px',
                    padding: '12px',
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    marginBottom: '10px',
                    position: 'relative'
                  }}>
                    {/* Remove Button */}
                    {onRemoveItem && (
                      <button
                        onClick={() => onRemoveItem(item._id, item.variantIndex)}
                        style={{
                          position: 'absolute',
                          top: '-8px',
                          right: '-8px',
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          background: '#ff4757',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(255, 71, 87, 0.4)'
                        }}
                      >
                        ✕
                      </button>
                    )}
                    <div style={{
                      width: '50px',
                      height: '50px',
                      background: `linear-gradient(135deg, ${item.color || '#667eea'}, ${item.color || '#764ba2'})`,
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      flexShrink: 0
                    }}>
                      {item.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: '0 0 3px 0', fontSize: '15px', fontWeight: '600', color: '#333' }}>
                        {item.name}
                      </p>
                      {item.selectedVariant && (
                        <p style={{ margin: '0 0 3px 0', fontSize: '12px', color: '#888' }}>
                          {item.selectedVariant.quantity} {item.selectedVariant.unit}
                        </p>
                      )}
                      <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                        ₹{itemPrice} × {item.quantity}
                      </p>
                      {/* Quantity Controls */}
                      {onUpdateQuantity && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                          <button
                            onClick={() => onUpdateQuantity(item._id, item.quantity - 1, item.variantIndex)}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: '#667eea',
                              color: 'white',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '16px',
                              fontWeight: 'bold'
                            }}
                          >
                            −
                          </button>
                          <span style={{ fontSize: '15px', fontWeight: '600', minWidth: '20px', textAlign: 'center' }}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => onUpdateQuantity(item._id, item.quantity + 1, item.variantIndex)}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: '#667eea',
                              color: 'white',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '16px',
                              fontWeight: 'bold'
                            }}
                          >
                            +
                          </button>
                        </div>
                      )}
                    </div>
                    <p style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#667eea', alignSelf: 'flex-start' }}>
                      ₹{(itemPrice * item.quantity).toFixed(2)}
                    </p>
                  </div>
                );
              })}
            </div>
          ))}

          {/* Bill Details */}
          <div style={{
            background: '#f8f9fa',
            padding: '20px',
            borderRadius: '15px',
            marginBottom: '20px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '15px', color: '#333' }}>
              Bill Details
            </h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
              <span style={{ fontSize: '14px', color: '#666' }}>Items Total</span>
              <span style={{ fontSize: '14px', fontWeight: '600' }}>₹{itemsTotal.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
              <span style={{ fontSize: '14px', color: '#666' }}>Delivery Charges</span>
              <span style={{ fontSize: '14px', fontWeight: '600' }}>₹{deliveryCharges.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
              <span style={{ fontSize: '14px', color: '#666' }}>Platform Fee</span>
              <span style={{ fontSize: '14px', fontWeight: '600' }}>₹{platformFee.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
              <span style={{ fontSize: '14px', color: '#666' }}>GST (5%)</span>
              <span style={{ fontSize: '14px', fontWeight: '600' }}>₹{gst.toFixed(2)}</span>
            </div>
            <div style={{
              borderTop: '2px solid #ddd',
              paddingTop: '15px',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <span style={{ fontSize: '18px', fontWeight: '700', color: '#333' }}>To Pay</span>
              <span style={{ fontSize: '20px', fontWeight: '700', color: '#667eea' }}>
                ₹{totalAmount.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Payment Methods */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: '#333' }}>
              Payment Method
            </h3>
            {['UPI', 'Card', 'Cash on Delivery'].map((method) => (
              <label
                key={method}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '15px',
                  background: paymentMethod === method ? '#e8f5ff' : '#f8f9fa',
                  border: paymentMethod === method ? '2px solid #667eea' : '2px solid transparent',
                  borderRadius: '12px',
                  marginBottom: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                <input
                  type="radio"
                  value={method}
                  checked={paymentMethod === method}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  style={{ marginRight: '12px', width: '18px', height: '18px' }}
                />
                <span style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>
                  {method === 'UPI' && '💳'} 
                  {method === 'Card' && '💳'} 
                  {method === 'Cash on Delivery' && '💵'} 
                  {' '}{method}
                </span>
              </label>
            ))}
          </div>

          {/* Place Order Button */}
          <button
            onClick={handlePlaceOrder}
            disabled={loading}
            style={{
              width: '100%',
              padding: '18px',
              background: loading 
                ? '#ccc' 
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '15px',
              fontSize: '18px',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 4px 15px rgba(102, 126, 234, 0.4)',
              marginBottom: '20px'
            }}
          >
            {loading ? '⏳ Placing Order...' : `Place Order • ₹${totalAmount.toFixed(2)}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutCart;
